import { AnnouncementFormValues } from "../adapters/formValuesToAnnouncement";
import { ContentBlock } from "../components/ContentBlocksEditor/ContentBlocksEditor";

let blockParseCounter = 0;
const generateParseId = () => `parsed-${++blockParseCounter}-${Date.now()}`;

function parseBody(doc: Document): ContentBlock[] {
	const nodes = Array.from(doc.body.childNodes);
	const blocks: ContentBlock[] = [];
	let textSegment = "";

	const flushText = () => {
		if (textSegment.trim()) {
			blocks.push({
				id: generateParseId(),
				type: "text",
				content: textSegment,
				file: null,
				previewUrl: null
			});
		}
		textSegment = "";
	};

	for (const node of nodes) {
		if (node instanceof HTMLImageElement) {
			flushText();
			blocks.push({
				id: generateParseId(),
				type: "image",
				content: node.getAttribute("src") || "",
				file: null,
				previewUrl: null
			});
		} else if (node instanceof HTMLVideoElement) {
			flushText();
			const source = node.querySelector("source");
			const src = source?.getAttribute("src") || node.getAttribute("src") || "";
			blocks.push({
				id: generateParseId(),
				type: "video",
				content: src,
				file: null,
				previewUrl: null
			});
		} else if (node instanceof HTMLElement) {
			textSegment += node.outerHTML;
		} else if (node.textContent?.trim()) {
			textSegment += node.textContent;
		}
	}

	flushText();
	return blocks;
}

export function contentBlocksToBlocks(
	contentBlocks: MeeAnnouncementContentBlock[]
): ContentBlock[] {
	return contentBlocks.map((cb) => ({
		id: generateParseId(),
		type: cb.type,
		content: cb.content,
		file: null,
		previewUrl: null
	}));
}

export function parseBodyToBlocks(
	body: string | null | undefined,
	contentBlocks?: MeeAnnouncementContentBlock[]
): ContentBlock[] {
	if (contentBlocks && contentBlocks.length > 0) {
		return contentBlocksToBlocks(contentBlocks);
	}

	if (!body || typeof body !== "string" || !body.trim()) {
		return [
			{
				id: generateParseId(),
				type: "text",
				content: "",
				file: null,
				previewUrl: null
			}
		];
	}

	const parser = new DOMParser();
	const doc = parser.parseFromString(body, "text/html");

	const blocks = parseBody(doc);

	if (blocks.length === 0) {
		blocks.push({
			id: generateParseId(),
			type: "text",
			content: "",
			file: null,
			previewUrl: null
		});
	}

	return blocks;
}

export function announcementToFormValues(
	announcement: MeeAnnouncement
): AnnouncementFormValues {
	const [scheduledDate = "", scheduledTime = ""] = announcement.scheduledAt
		? announcement.scheduledAt.split(/T| /)
		: [];
	const [expiryDate = "", expiryTime = ""] = announcement.expiresAt
		? announcement.expiresAt.split(/T| /)
		: [];

	return {
		notificationType: announcement.body ? "announcement" : "push",
		title: announcement.title,
		body: announcement.body ?? "",
		heroImage: null,
		pushTitle: announcement.pushTitle ?? "",
		pushBody: announcement.pushBody ?? "",
		audienceType: announcement.audienceType,
		audiencePlatform: announcement.audiencePlatform ?? "both",
		audienceUserIds: announcement.audienceUserIds,
		scheduleType: announcement.scheduledAt ? "scheduled" : "now",
		scheduledDate,
		scheduledTime: scheduledTime.slice(0, 5),
		hasExpiry: !!announcement.expiresAt,
		expiryDate,
		expiryTime: expiryTime.slice(0, 5),
		blockFiles: [],
		contentBlocks: announcement.contentBlocks ?? []
	};
}

export function formatAudience(announcement: MeeAnnouncement): string {
	if (announcement.audienceType === "everyone") return "Everyone";
	if (announcement.audienceType === "platform") {
		if (announcement.audiencePlatform === "ios") return "iOS";
		if (announcement.audiencePlatform === "android") return "Android";
		return "Both platforms";
	}
	if (announcement.audienceType === "groups") return "By group";
	const count = announcement.audienceUserIds.length;
	return `${count} user${count !== 1 ? "s" : ""}`;
}
